/* eslint-disable react-native/no-inline-styles */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { DocketStatus, NotifierDocketItems } from '@oolio-group/domain';
import BumpItemIndicator from './BumpItemIndicator';
import DocketItemTimer from './DocketItemTimer';
import { useInvokeWithDelay } from '../../../hooks/useInvokeWithDelay';
import { DocketBumper } from '../../../screens/Main/Dockets/DocketBumper';
import theme from '../../../common/theme';
import DocketItemStyles from '../DocketItem.styles';
import { useColors } from '../../../hooks/useColors';
import Icon from '../../Icon/Icon';
import { headerColorSubject } from './DocketItemTimer';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { selectedDocketVar, bulkSelectVar } from '../../../state/apolloVars';
import { useReactiveVar } from '@apollo/client';
import { useFeatureEnabled } from '../../../hooks/useFeatureEnabled';
import { FEATURES } from '../../../common/constants';

export interface DocketItemHeaderProps {
  docketId: string;
  orderTypeName: string;
  salesChannelName: string;
  orderNumber: string;
  docketName: string;
  createdTime: string;
  onPressDocketHeader: () => Promise<void>;
  status: DocketStatus;
  docketHeaderColor?: string;
  docketStatus: DocketStatus;
  isTraining?: boolean;
  notifierItems?: NotifierDocketItems[];
  onSelectDocket?: () => void;
  enablePrint?: boolean;
  printKitchenDocket?: (
    docketId: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => void;
}

const DocketItemHeader: React.FC<DocketItemHeaderProps> = ({
  docketId,
  orderTypeName,
  onPressDocketHeader,
  orderNumber,
  docketName,
  createdTime,
  status,
  docketHeaderColor = theme.colors.primary,
  docketStatus,
  salesChannelName,
  isTraining,
  notifierItems,
  onSelectDocket,
  enablePrint,
  printKitchenDocket,
}) => {
  const { colors } = useColors();
  const context = useContext(DocketBumper);
  const { loading: bumpLoading, invokeFunction: invokeBump } =
    useInvokeWithDelay({ callBack: onPressDocketHeader });
  const touchable = useRef(null);

  const [milestoneColor, setMilestoneColor] = useState<string | undefined>(
    () => headerColorSubject.getValue()[docketId],
  );

  const isBulkMode = useReactiveVar(bulkSelectVar);
  const selectedDockets = useReactiveVar(selectedDocketVar);
  const isTagsEnabled = useFeatureEnabled(FEATURES.TAG_DOCKETS);

  const isSelected = Object.keys(selectedDockets).includes(docketId);

  const styles = DocketItemStyles({ isSelected: isBulkMode && isSelected });

  const handleOnInvokeBump = useCallback(() => {
    if (context.current && context.current?.includes(docketId)) {
      context.current = context?.current?.filter(id => id != docketId) || [];
    } else {
      context.current = [...(context?.current || []), docketId];
    }
    invokeBump();
  }, [context, docketId, invokeBump]);

  const handlePrintKitchenDocket = useCallback(() => {
    if (enablePrint && printKitchenDocket) {
      printKitchenDocket(docketId, status, notifierItems);
    }
  }, [docketId, enablePrint, notifierItems, printKitchenDocket, status]);

  const docketColor = milestoneColor || docketHeaderColor;

  useEffect(() => {
    const sub: Subscription = headerColorSubject
      .pipe(
        map(colors => colors[docketId]),
        distinctUntilChanged(),
      )
      .subscribe(setMilestoneColor);

    return () => sub.unsubscribe();
  }, [docketId]);

  useEffect(() => {
    if (
      docketStatus == DocketStatus.CREATED &&
      context.current?.includes(docketId) &&
      !bumpLoading
    ) {
      invokeBump();
    }
  }, [bumpLoading, context, docketId, invokeBump, docketStatus, orderNumber]);

  const headerText = salesChannelName
    ? `${salesChannelName} - ${orderTypeName}`
    : orderTypeName;

  const headerBgColor =
    status == DocketStatus.CREATED ? docketColor : colors.green;

  return (
    <TouchableOpacity
      testID="docket-header"
      onPress={handleOnInvokeBump}
      disabled={status == DocketStatus.COMPLETED || isBulkMode || isSelected}
      style={[
        enablePrint || (status == DocketStatus.CREATED && isTagsEnabled)
          ? styles.headerSettings
          : styles.header,
        {
          backgroundColor: headerBgColor,
        },
        isTraining && styles.headerSpacingForTraining,
        isBulkMode && isSelected && styles.headerSelected,
      ]}
    >
      {context?.current?.includes(docketId) && bumpLoading ? (
        <BumpItemIndicator />
      ) : (
        <>
          <View style={styles.headerContent}>
            {isBulkMode && (
              <TouchableOpacity
                testID="btn-select"
                onPress={onSelectDocket}
                style={styles.btnContainer}
              >
                <Icon
                  name={isSelected ? 'check-square' : 'square-full'}
                  color={isSelected ? colors.positive : colors.white}
                />
              </TouchableOpacity>
            )}

            <View style={styles.rowGroup}>
              <View style={styles.row}>
                <Text style={styles.text}>{headerText}</Text>
                <Text style={styles.text}>{orderNumber}</Text>
              </View>
              <View style={styles.row}>
                <Text
                  numberOfLines={1}
                  style={[styles.text, { maxWidth: '60%' }]}
                  ellipsizeMode="tail"
                >
                  {docketName}
                </Text>
                <DocketItemTimer
                  createdTime={createdTime}
                  status={status}
                  docketId={docketId}
                />
              </View>
            </View>
            {!isBulkMode &&
              status !== DocketStatus.COMPLETED &&
              isTagsEnabled && (
                <TouchableOpacity
                  testID="btn-settings"
                  onPress={onSelectDocket}
                  ref={touchable}
                  style={styles.btnContainer}
                >
                  <Icon name={'ellipsis-v'} color={colors.white} />
                </TouchableOpacity>
              )}
            {enablePrint &&
              (status === DocketStatus.COMPLETED || !isTagsEnabled) && (
                <TouchableOpacity
                  testID="btn-print"
                  onPress={handlePrintKitchenDocket}
                  ref={touchable}
                  style={styles.btnContainer}
                >
                  <Icon name={'print'} color={colors.white} />
                </TouchableOpacity>
              )}
          </View>
        </>
      )}
    </TouchableOpacity>
  );
};

export default DocketItemHeader;
